import { useState, useEffect } from 'react';
import { MobileSelect } from '@/components/ui';
import { useUnassignedPhotos } from '../hooks';
import { useProjectOptions } from '@/hooks';
import { getPhotoUrl } from '../services/photoService';

export function UnassignedPhotosList() {
  const { 
    photos, 
    isLoading, 
    error, 
    assignToProject, 
    dismissPhoto,
    refetch 
  } = useUnassignedPhotos();
  
  const { projects } = useProjectOptions(['planned', 'in_progress']);
  
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [selectedProjects, setSelectedProjects] = useState<Record<string, string>>({});
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());

  // Load signed URLs
  useEffect(() => {
    const loadUrls = async () => {
      for (const photo of photos) {
        if (!signedUrls[photo.id]) {
          try {
            const url = await getPhotoUrl(photo.filePath);
            setSignedUrls((prev) => ({ ...prev, [photo.id]: url }));
          } catch (error) {
            console.error(`Error loading URL for photo ${photo.id}:`, error);
          }
        }
      }
    };
    loadUrls();
  }, [photos, signedUrls]);

  // Initialize selected projects with suggestions
  useEffect(() => {
    const initial: Record<string, string> = {};
    photos.forEach((photo) => {
      if (photo.suggestedProjectId && !selectedProjects[photo.id]) {
        initial[photo.id] = photo.suggestedProjectId;
      }
    });
    if (Object.keys(initial).length > 0) {
      setSelectedProjects((prev) => ({ ...prev, ...initial }));
    }
  }, [photos, selectedProjects]);

  const handleAssign = async (photoId: string) => {
    const projectId = selectedProjects[photoId];
    if (!projectId) return;

    setProcessingIds((prev) => new Set(prev).add(photoId));
    try {
      await assignToProject(photoId, projectId);
    } catch (error) {
      console.error('Error assigning photo:', error);
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(photoId);
        return next;
      });
    }
  };

  const handleDismiss = async (photoId: string) => {
    setProcessingIds((prev) => new Set(prev).add(photoId));
    try {
      await dismissPhoto(photoId);
    } catch (error) {
      console.error('Error dismissing photo:', error);
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(photoId);
        return next;
      });
    }
  };

  const confidenceColors = {
    high: 'bg-green-100 text-green-700 border-green-200',
    medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    low: 'bg-orange-100 text-orange-700 border-orange-200',
    none: 'bg-secondary-100 text-secondary-700 border-secondary-200',
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-600 p-4 rounded-lg">
        <p>{error}</p>
        <button
          onClick={refetch}
          className="mt-2 text-sm underline hover:no-underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className="text-center py-12 bg-secondary-50 rounded-lg">
        <svg
          className="w-12 h-12 mx-auto text-secondary-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p className="mt-2 text-secondary-600">All photos have been assigned!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-secondary-600">
          {photos.length} photo{photos.length !== 1 ? 's' : ''} pending assignment
        </p>
        <button
          onClick={refetch}
          className="text-sm text-primary-600 hover:text-primary-700"
        >
          Refresh
        </button>
      </div>

      <div className="space-y-4">
        {photos.map((photo) => {
          const isProcessing = processingIds.has(photo.id);
          const confidence = (photo.matchConfidence || 'none') as 'high' | 'medium' | 'low' | 'none';

          return (
            <div
              key={photo.id}
              className="bg-white border border-secondary-200 rounded-lg p-4 flex gap-4"
            >
              {/* Photo thumbnail */}
              <div className="w-24 h-24 flex-shrink-0 bg-secondary-100 rounded-lg overflow-hidden">
                {signedUrls[photo.id] ? (
                  <img
                    src={signedUrls[photo.id]}
                    alt={photo.fileName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-secondary-400 animate-pulse"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                )}
              </div>

              {/* Photo details */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-secondary-900 truncate">
                      {photo.fileName}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      {photo.takenAt && (
                        <span className="text-sm text-secondary-500">
                          {new Date(photo.takenAt).toLocaleDateString()}
                        </span>
                      )}
                      {photo.hasCoordinates && (
                        <span className="inline-flex items-center gap-1 text-xs text-green-600">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                          </svg>
                          GPS
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Match confidence badge */}
                  {photo.suggestedProjectId && (
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full border ${confidenceColors[confidence]}`}
                    >
                      {confidence} match
                    </span>
                  )}
                </div>

                {/* Suggested project info */}
                {photo.suggestedProjectName && (
                  <div className="mt-2 p-2 bg-secondary-50 rounded text-sm">
                    <span className="text-secondary-500">Suggested: </span>
                    <span className="font-medium text-secondary-900">
                      {photo.suggestedProjectName}
                    </span>
                    {photo.matchDistanceM && (
                      <span className="text-secondary-500">
                        {' '}({Math.round(photo.matchDistanceM)}m away)
                      </span>
                    )}
                  </div>
                )}

                {/* Project selector and actions */}
<div className="mt-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
  <div className="flex-1">
    <MobileSelect
      value={selectedProjects[photo.id] || ''}
      onChange={(value) =>
        setSelectedProjects((prev) => ({
          ...prev,
          [photo.id]: value,
        }))
      }
      disabled={isProcessing}
      placeholder="Select a project..."
      options={[
        ...(photo.suggestedProjectId
          ? [{
              value: photo.suggestedProjectId,
              label: `✓ ${photo.suggestedProjectName}`,
              sublabel: 'Suggested match',
            }]
          : []),
        ...projects
          .filter((p) => p.id !== photo.suggestedProjectId)
          .map((project) => ({
            value: project.id,
            label: project.name,
            sublabel: project.customerName || undefined,
          })),
      ]}
    />
  </div>

  <div className="flex gap-2">
    <button
      onClick={() => handleAssign(photo.id)}
      disabled={!selectedProjects[photo.id] || isProcessing}
      className="flex-1 sm:flex-none px-4 py-3 md:py-2 bg-primary-600 text-white text-base md:text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
    >
      {isProcessing ? 'Saving...' : 'Assign'}
    </button>

    <button
      onClick={() => handleDismiss(photo.id)}
      disabled={isProcessing}
      className="flex-1 sm:flex-none px-4 py-3 md:py-2 border border-secondary-300 text-secondary-700 text-base md:text-sm font-medium rounded-lg hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
    >
      Skip
    </button>
  </div>
</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}